import { Component, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserX, UserCheck, RefreshCw, Trash2, Copy, Plus, RotateCcw, Download } from "lucide-react";

function mask(email) {
  if (!email) return "";
  const [u, d] = email.split("@");
  if (!d) return email;
  return u.slice(0, 2) + "***@" + d;
}

const LETTER_TYPES = ["equifax","experian","transunion","furnisher","debt_validation","mov","follow_up","arbitration"];

export default function Admin() {
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [reasons, setReasons] = useState("");
  const [disclaimer, setDisclaimer] = useState("");
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState({ code: "", max_uses: 0, expires_at: "", note: "" });
  const [templates, setTemplates] = useState([]);
  const [activeTpl, setActiveTpl] = useState("equifax");
  const [tplBody, setTplBody] = useState("");
  const [wh, setWh] = useState({ enabled: false, has_secret: false, endpoint_path: "" });
  const [whSecret, setWhSecret] = useState("");
  const [whEvents, setWhEvents] = useState([]);
  // Skool member allowlist
  const [skoolRows, setSkoolRows] = useState([]);
  const [skoolCounts, setSkoolCounts] = useState({ approved: 0, revoked: 0, pending: 0, total: 0 });
  const [skoolFilter, setSkoolFilter] = useState("all");
  const [skoolSearch, setSkoolSearch] = useState("");
  const [skoolCsv, setSkoolCsv] = useState("");
  const [skoolCsvBusy, setSkoolCsvBusy] = useState(false);
  const [skoolNewEmail, setSkoolNewEmail] = useState("");
  const [skoolEditingId, setSkoolEditingId] = useState(null);
  const [skoolEditEmail, setSkoolEditEmail] = useState("");
  const [aiFlag, setAiFlag] = useState(false);
  const [tab, setTab] = useState("users");

  const load = async () => {
    const [s,u,a,r,d,c,t,ws,we,ff] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/users"),
      api.get("/admin/audit"),
      api.get("/admin/reasons"),
      api.get("/admin/disclaimer"),
      api.get("/admin/invite-codes"),
      api.get("/admin/templates"),
      api.get("/admin/webhook/settings"),
      api.get("/admin/webhook/events"),
      api.get("/admin/feature-flags"),
    ]);
    setStats(s.data); setUsers(u.data); setAudit(a.data);
    setReasons((r.data.values || []).join("\n"));
    setDisclaimer(d.data.text || "");
    setCodes(c.data);
    setTemplates(t.data);
    const cur = t.data.find(x=>x.letter_type===activeTpl) || t.data[0];
    if (cur) { setActiveTpl(cur.letter_type); setTplBody(cur.body); }
    setWh(ws.data);
    setWhEvents(we.data);
    setAiFlag(!!ff.data?.ai_suggestions_enabled);
  };

  useEffect(()=>{ load().catch(()=>toast.error("Admin required")); /* eslint-disable-next-line */ }, []);

  const toggleUser = async (u) => {
    if (u.disabled) await api.post(`/admin/users/${u.id}/restore`);
    else await api.post(`/admin/users/${u.id}/revoke`, { reason: "manual" });
    toast.success("Updated"); load();
  };

  const saveReasons = async () => { await api.put("/admin/reasons", { values: reasons.split("\n").map(x=>x.trim()).filter(Boolean) }); toast.success("Reasons saved"); load(); };
  const saveDisclaimer = async () => { await api.put("/admin/disclaimer", { text: disclaimer }); toast.success("Disclaimer saved"); load(); };
  const saveAiFlag = async () => { await api.put("/admin/feature-flags", { ai_suggestions_enabled: !!aiFlag }); toast.success(`AI assistant flag: ${aiFlag ? "ON" : "OFF"}`); load(); };

  // Invite codes
  const createCode = async () => {
    const body = { code: newCode.code || undefined, max_uses: Number(newCode.max_uses) || 0, expires_at: newCode.expires_at ? new Date(newCode.expires_at).toISOString() : null, note: newCode.note };
    await api.post("/admin/invite-codes", body);
    setNewCode({ code:"", max_uses:0, expires_at:"", note:"" });
    toast.success("Invite code created"); load();
  };
  const rotateCode = async (id) => { const r = await api.post(`/admin/invite-codes/${id}/rotate`); toast.success(`New code: ${r.data.code}`); load(); };
  const toggleCode = async (row) => { await api.post(`/admin/invite-codes/${row.id}/${row.disabled?"enable":"disable"}`); load(); };
  const deleteCode = async (id) => { if (!window.confirm("Delete this code?")) return; await api.delete(`/admin/invite-codes/${id}`); load(); };
  const copyCode = (c) => { navigator.clipboard.writeText(c); toast.success("Copied"); };

  // Templates
  const pickTpl = (lt) => { setActiveTpl(lt); const t = templates.find(x=>x.letter_type===lt); setTplBody(t?.body || ""); };
  const saveTpl = async () => { await api.put(`/admin/templates/${activeTpl}`, { body: tplBody }); toast.success("Template saved"); load(); };
  const resetTpl = async () => { const r = await api.post(`/admin/templates/${activeTpl}/reset`); setTplBody(r.data.body); toast.success("Reset to default"); load(); };

  // Webhook
  const saveWebhook = async () => {
    const body = { enabled: wh.enabled };
    if (whSecret) body.secret = whSecret;
    await api.put("/admin/webhook/settings", body);
    setWhSecret("");
    toast.success("Webhook settings saved"); load();
  };

  // Skool members
  const loadSkool = async () => {
    const params = new URLSearchParams();
    if (skoolFilter !== "all") params.set("status", skoolFilter);
    if (skoolSearch.trim()) params.set("q", skoolSearch.trim());
    const r = await api.get(`/admin/skool-members${params.toString() ? "?" + params.toString() : ""}`);
    setSkoolRows(r.data.rows || []);
    setSkoolCounts(r.data.counts || { approved: 0, revoked: 0, pending: 0, total: 0 });
  };
  const skoolAdd = async () => {
    if (!skoolNewEmail.trim()) return;
    try {
      await api.post("/admin/skool-members", { email: skoolNewEmail.trim(), status: "approved" });
      setSkoolNewEmail(""); toast.success("Member approved"); loadSkool();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };
  const skoolRevoke = async (id) => {
    if (!window.confirm("Revoke access for this member?")) return;
    try { await api.post(`/admin/skool-members/${id}/revoke`); toast.success("Access revoked"); loadSkool(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
  };
  const skoolRestore = async (id) => {
    try { await api.post(`/admin/skool-members/${id}/restore`); toast.success("Access restored"); loadSkool(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };
  const skoolStartEdit = (row) => { setSkoolEditingId(row.id); setSkoolEditEmail(row.email); };
  const skoolCancelEdit = () => { setSkoolEditingId(null); setSkoolEditEmail(""); };
  const skoolSaveEmail = async () => {
    try {
      await api.put(`/admin/skool-members/${skoolEditingId}/email`, { email: skoolEditEmail.trim() });
      toast.success("Email corrected"); skoolCancelEdit(); loadSkool();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const skoolImportCsv = async () => {
    if (!skoolCsv.trim()) return toast.error("Paste or upload a CSV first");
    setSkoolCsvBusy(true);
    try {
      const r = await api.post("/admin/skool-members/import-csv", { csv: skoolCsv, default_status: "approved" });
      const errs = r.data.errors || [];
      toast.success(`Imported ${r.data.imported}, skipped ${r.data.skipped}${errs.length ? `, ${errs.length} errors` : ""}`);
      if (errs.length) console.warn("Skool CSV import errors:", errs);
      setSkoolCsv(""); loadSkool();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setSkoolCsvBusy(false); }
  };
  const skoolUploadFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setSkoolCsv(String(ev.target?.result || ""));
    reader.readAsText(file);
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Administrator</div>
          <h1 className="text-3xl font-semibold text-slate-900 mt-1">Admin Console</h1>
          <p className="text-slate-500 text-sm">Sensitive data is masked. Admins cannot view SSNs or full account numbers.</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Users" v={stats.users} t="admin-stat-users"/>
        <Stat label="Reports" v={stats.reports} t="admin-stat-reports"/>
        <Stat label="Documents" v={stats.documents} t="admin-stat-docs"/>
        <Stat label="CFPB Drafts" v={stats.cfpb_drafts} t="admin-stat-cfpb"/>
        <Stat label="Audit Events" v={stats.audit} t="admin-stat-audit"/>
      </div>

      <Link to="/app/admin/letter-library" className="mt-4 inline-flex items-center min-h-[44px] rounded-xl border border-purple-200 bg-purple-50 px-4 text-sm font-semibold text-purple-800" data-testid="admin-letter-library-link">Open Letter Library Manager</Link>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="bg-slate-50 border border-slate-200 flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="codes" data-testid="admin-tab-codes">Invite Codes</TabsTrigger>
          <TabsTrigger value="templates" data-testid="admin-tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="webhook" data-testid="admin-tab-webhook">Skool Webhook</TabsTrigger>
          <TabsTrigger value="skool" onClick={loadSkool} data-testid="admin-tab-skool">Skool Members</TabsTrigger>
          <TabsTrigger value="mailings" data-testid="admin-tab-mailings">Mailings</TabsTrigger>
          <TabsTrigger value="credits" data-testid="admin-tab-credits">Credits</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit">Activity Log</TabsTrigger>
          <TabsTrigger value="reasons" data-testid="admin-tab-reasons">Reasons</TabsTrigger>
          <TabsTrigger value="disclaimer" data-testid="admin-tab-disclaimer">Disclaimer</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <Card className="glass rounded-2xl mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="p-3">User</th><th className="p-3">Email</th><th className="p-3">State</th><th className="p-3">Role</th><th className="p-3">Skool</th><th className="p-3">Status</th><th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u=>(
                    <tr key={u.id} className="border-b border-slate-200">
                      <td className="p-3 text-slate-900">{u.first_name} {u.last_name}</td>
                      <td className="p-3 text-slate-700">{mask(u.email)}</td>
                      <td className="p-3 text-slate-700">{u.state}</td>
                      <td className="p-3"><span className="stage-pill rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest">{u.role}</span></td>
                      <td className="p-3 text-slate-500 text-xs">{u.skool_status || "—"}</td>
                      <td className="p-3">{u.disabled ? <span className="badge-danger rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest">Revoked</span> : <span className="badge-success rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest">Active</span>}</td>
                      <td className="p-3">
                        {u.role !== "admin" && (
                          <Button size="sm" variant="outline" className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800" onClick={()=>toggleUser(u)} data-testid={`admin-toggle-user-${u.id}`}>
                            {u.disabled ? <><UserCheck size={13} className="mr-1"/>Restore</> : <><UserX size={13} className="mr-1"/>Revoke</>}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="codes">
          <div className="grid lg:grid-cols-3 gap-4 mt-4">
            <Card className="glass rounded-2xl lg:col-span-1">
              <CardContent className="p-6 space-y-3">
                <div className="text-slate-900 font-semibold">Create invite code</div>
                <div>
                  <Label className="text-slate-700 text-xs">Custom code (optional)</Label>
                  <Input value={newCode.code} onChange={(e)=>setNewCode(s=>({...s, code: e.target.value.toUpperCase()}))} data-testid="admin-newcode-code" placeholder="Auto-generated if blank" className="mt-1 bg-slate-50 border-slate-200 text-slate-900 uppercase font-mono"/>
                </div>
                <div>
                  <Label className="text-slate-700 text-xs">Max uses (0 = unlimited)</Label>
                  <Input type="number" min="0" value={newCode.max_uses} onChange={(e)=>setNewCode(s=>({...s, max_uses: e.target.value}))} data-testid="admin-newcode-max" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"/>
                </div>
                <div>
                  <Label className="text-slate-700 text-xs">Expires at (optional)</Label>
                  <Input type="date" value={newCode.expires_at} onChange={(e)=>setNewCode(s=>({...s, expires_at: e.target.value}))} data-testid="admin-newcode-expires" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"/>
                </div>
                <div>
                  <Label className="text-slate-700 text-xs">Note</Label>
                  <Input value={newCode.note} onChange={(e)=>setNewCode(s=>({...s, note: e.target.value}))} data-testid="admin-newcode-note" placeholder="e.g. Cohort 12" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"/>
                </div>
                <Button onClick={createCode} className="btn-primary rounded-xl w-full" data-testid="admin-newcode-create"><Plus size={14} className="mr-1"/>Create code</Button>
              </CardContent>
            </Card>

            <Card className="glass rounded-2xl lg:col-span-2">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                    <tr><th className="p-3">Code</th><th className="p-3">Uses</th><th className="p-3">Expires</th><th className="p-3">Note</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
                  </thead>
                  <tbody>
                    {codes.map(c=>(
                      <tr key={c.id} className="border-b border-slate-200">
                        <td className="p-3 text-slate-900 font-mono">{c.code}</td>
                        <td className="p-3 text-slate-700">{c.uses}{c.max_uses?` / ${c.max_uses}`:""}</td>
                        <td className="p-3 text-slate-700 text-xs">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</td>
                        <td className="p-3 text-slate-700 text-xs">{c.note || "—"}</td>
                        <td className="p-3">{c.disabled ? <span className="badge-danger rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest">Disabled</span> : <span className="badge-success rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest">Active</span>}</td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="text-slate-700" onClick={()=>copyCode(c.code)} data-testid={`admin-code-copy-${c.id}`}><Copy size={13}/></Button>
                            <Button size="sm" variant="ghost" className="text-purple-700" onClick={()=>rotateCode(c.id)} data-testid={`admin-code-rotate-${c.id}`}><RefreshCw size={13}/></Button>
                            <Button size="sm" variant="ghost" className={c.disabled?"text-emerald-300":"text-amber-300"} onClick={()=>toggleCode(c)} data-testid={`admin-code-toggle-${c.id}`}>{c.disabled?"Enable":"Disable"}</Button>
                            <Button size="sm" variant="ghost" className="text-red-300" onClick={()=>deleteCode(c.id)} data-testid={`admin-code-delete-${c.id}`}><Trash2 size={13}/></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {codes.length===0 && <tr><td colSpan="6" className="p-6 text-slate-500 text-center">No invite codes yet.</td></tr>}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="templates">
          <Card className="glass rounded-2xl mt-4">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-slate-900 font-semibold">Letter templates</div>
                  <p className="text-xs text-slate-500 mt-1">Placeholders: <span className="font-mono text-purple-700">{"{account_block} {full_name} {first_name} {last_name} {consumer_block} {today} {recipient} {title}"}</span></p>
                </div>
                <Select value={activeTpl} onValueChange={pickTpl}>
                  <SelectTrigger className="w-[260px] bg-slate-50 border-slate-200 text-slate-900" data-testid="admin-tpl-select"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-white text-slate-900 border-slate-200">
                    {LETTER_TYPES.map(lt=>{
                      const t = templates.find(x=>x.letter_type===lt);
                      return <SelectItem key={lt} value={lt}>{t?.title || lt}{t?.is_custom?" · custom":""}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <Textarea rows={20} value={tplBody} onChange={(e)=>setTplBody(e.target.value)} data-testid="admin-tpl-body" className="mt-4 bg-slate-50 border-slate-200 text-slate-900 font-mono text-[13px]"/>
              <div className="mt-3 flex gap-2">
                <Button onClick={saveTpl} className="btn-primary rounded-xl" data-testid="admin-tpl-save">Save template</Button>
                <Button onClick={resetTpl} variant="outline" className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800" data-testid="admin-tpl-reset"><RotateCcw size={14} className="mr-1"/>Reset to default</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="webhook">
          <div className="grid lg:grid-cols-3 gap-4 mt-4">
            <Card className="glass rounded-2xl lg:col-span-1">
              <CardContent className="p-6 space-y-4">
                <div className="text-slate-900 font-semibold">Skool / Zapier webhook</div>
                <div className="text-xs text-slate-500">Endpoint (POST): <span className="font-mono text-purple-700">{wh.endpoint_path}</span></div>
                <div className="flex items-center gap-3">
                  <Switch checked={wh.enabled} onCheckedChange={(v)=>setWh(s=>({...s, enabled: v}))} data-testid="admin-wh-enabled"/>
                  <span className="text-sm text-slate-800">{wh.enabled ? "Enabled" : "Disabled"}</span>
                </div>
                <div>
                  <Label className="text-slate-700 text-xs">Webhook secret {wh.has_secret && <span className="text-emerald-300 ml-1">(set)</span>}</Label>
                  <Input type="password" value={whSecret} onChange={(e)=>setWhSecret(e.target.value)} data-testid="admin-wh-secret" placeholder={wh.has_secret ? "•••••••• (leave blank to keep)" : "paste secret from Skool/Zapier"} className="mt-1 bg-slate-50 border-slate-200 text-slate-900 font-mono"/>
                </div>
                <Button onClick={saveWebhook} className="btn-primary rounded-xl w-full" data-testid="admin-wh-save">Save webhook settings</Button>
                <Link to="/app/admin/zapier-guide" data-testid="admin-wh-zapier-guide-link">
                  <Button variant="outline" className="rounded-xl w-full border-slate-200 bg-slate-50/70 text-slate-800 mt-1">Open Zapier setup guide →</Button>
                </Link>
                <div className="text-[11px] text-slate-400 leading-relaxed">
                  Expected events: <b>new_paid_member</b>, <b>membership_cancelled</b>, <b>membership_expired</b>, <b>member_removed</b>, <b>membership_restored</b>.
                  HMAC-SHA256 signature required in <span className="font-mono">X-Skool-Signature</span> header; duplicate events (by <span className="font-mono">X-Event-Id</span>) are ignored.
                </div>
              </CardContent>
            </Card>

            <Card className="glass rounded-2xl lg:col-span-2">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                    <tr><th className="p-3">Time</th><th className="p-3">Event</th><th className="p-3">Email</th><th className="p-3">Result</th></tr>
                  </thead>
                  <tbody data-testid="admin-webhook-events">
                    {whEvents.map(e=>(
                      <tr key={e.id} className="border-b border-slate-200">
                        <td className="p-3 text-slate-700 text-xs">{new Date(e.created_at).toLocaleString()}</td>
                        <td className="p-3 text-slate-900">{e.event_type}</td>
                        <td className="p-3 text-slate-700">{mask(e.email)}</td>
                        <td className="p-3 text-purple-700 text-xs">{e.result}</td>
                      </tr>
                    ))}
                    {whEvents.length===0 && <tr><td colSpan="4" className="p-6 text-slate-500 text-center">No webhook events yet — enable the webhook and connect Skool/Zapier.</td></tr>}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="skool">
          <div className="grid lg:grid-cols-3 gap-4 mt-4">
            <Card className="glass rounded-2xl lg:col-span-1">
              <CardContent className="p-5 space-y-4">
                <div>
                  <div className="eyebrow">Approved · Revoked · Pending</div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <span className="badge-success rounded-full px-2 py-0.5 text-[11px]" data-testid="skool-count-approved">{skoolCounts.approved} approved</span>
                    <span className="badge-danger rounded-full px-2 py-0.5 text-[11px]" data-testid="skool-count-revoked">{skoolCounts.revoked} revoked</span>
                    <span className="stage-pill rounded-full px-2 py-0.5 text-[11px]" data-testid="skool-count-pending">{skoolCounts.pending} pending</span>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-700 text-xs">Add a member manually</Label>
                  <div className="flex gap-2 mt-1">
                    <Input placeholder="member@example.com" value={skoolNewEmail} onChange={(e)=>setSkoolNewEmail(e.target.value)} data-testid="skool-add-email" className="bg-slate-50 border-slate-200 text-slate-900"/>
                    <Button onClick={skoolAdd} className="btn-primary rounded-xl" data-testid="skool-add-submit"><Plus size={14} className="mr-1"/>Approve</Button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">Emails are normalized (lowercase, whitespace stripped).</p>
                </div>
                <div>
                  <Label className="text-slate-700 text-xs">Import small-launch list (CSV)</Label>
                  <input type="file" accept=".csv,text/csv" onChange={(e)=>skoolUploadFile(e.target.files?.[0])} data-testid="skool-csv-file" className="block mt-1 text-xs text-slate-700 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-purple-100 file:text-purple-700"/>
                  <Textarea rows={6} placeholder="email\nfriend@example.com\nother@example.com" value={skoolCsv} onChange={(e)=>setSkoolCsv(e.target.value)} data-testid="skool-csv-textarea" className="mt-2 bg-slate-50 border-slate-200 text-slate-900 text-xs font-mono"/>
                  <Button onClick={skoolImportCsv} disabled={skoolCsvBusy || !skoolCsv.trim()} className="btn-primary rounded-xl mt-2 w-full" data-testid="skool-csv-import"><Download size={14} className="mr-1"/>{skoolCsvBusy ? "Importing…" : "Import as approved"}</Button>
                  <p className="text-[11px] text-slate-500 mt-2">Include ONLY the selected small-launch members. A single column with header <code>email</code> works; a headerless column-1 CSV also works. Comment lines starting with <code>#</code> are skipped.</p>
                </div>
              </CardContent>
            </Card>
            <Card className="glass rounded-2xl lg:col-span-2">
              <CardContent className="p-0">
                <div className="p-4 border-b border-slate-200 flex items-center gap-2 flex-wrap">
                  <Select value={skoolFilter} onValueChange={(v)=>{ setSkoolFilter(v); }}>
                    <SelectTrigger className="w-40 bg-slate-50 border-slate-200 text-slate-900" data-testid="skool-filter"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="revoked">Revoked</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input placeholder="Search email…" value={skoolSearch} onChange={(e)=>setSkoolSearch(e.target.value)} onKeyDown={(e)=>e.key === "Enter" && loadSkool()} data-testid="skool-search" className="max-w-xs bg-slate-50 border-slate-200 text-slate-900"/>
                  <Button variant="outline" onClick={loadSkool} className="rounded-xl" data-testid="skool-refresh"><RefreshCw size={13} className="mr-1"/>Refresh</Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                      <tr><th className="p-3">Email</th><th className="p-3">Status</th><th className="p-3">Source</th><th className="p-3">Last event</th><th className="p-3">Actions</th></tr>
                    </thead>
                    <tbody data-testid="skool-members-tbody">
                      {skoolRows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500 text-sm">No members yet. Import a CSV or add a member manually.</td></tr>}
                      {skoolRows.map(row => (
                        <tr key={row.id} className="border-b border-slate-200" data-testid={`skool-row-${row.id}`}>
                          <td className="p-3 text-slate-900 text-xs">
                            {skoolEditingId === row.id ? (
                              <div className="flex gap-1">
                                <Input value={skoolEditEmail} onChange={(e)=>setSkoolEditEmail(e.target.value)} data-testid={`skool-edit-input-${row.id}`} className="bg-slate-50 border-slate-200 text-slate-900 text-xs"/>
                                <Button size="sm" onClick={skoolSaveEmail} className="btn-primary rounded-xl text-xs" data-testid={`skool-edit-save-${row.id}`}>Save</Button>
                                <Button size="sm" variant="ghost" onClick={skoolCancelEdit} className="rounded-xl text-xs">Cancel</Button>
                              </div>
                            ) : row.email}
                          </td>
                          <td className="p-3">
                            {row.status === "approved" && <span className="badge-success rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest">Approved</span>}
                            {row.status === "revoked" && <span className="badge-danger rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest">Revoked</span>}
                            {row.status === "pending" && <span className="stage-pill rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest">Pending</span>}
                          </td>
                          <td className="p-3 text-slate-500 text-xs">{row.source}</td>
                          <td className="p-3 text-slate-500 text-xs">{row.last_event_type || "—"}</td>
                          <td className="p-3">
                            <div className="flex gap-1 flex-wrap">
                              {row.status !== "approved" && <Button size="sm" variant="outline" onClick={()=>skoolRestore(row.id)} className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800 text-xs" data-testid={`skool-restore-${row.id}`}><UserCheck size={12} className="mr-1"/>Restore</Button>}
                              {row.status !== "revoked" && <Button size="sm" variant="outline" onClick={()=>skoolRevoke(row.id)} className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800 text-xs" data-testid={`skool-revoke-${row.id}`}><UserX size={12} className="mr-1"/>Revoke</Button>}
                              {skoolEditingId !== row.id && <Button size="sm" variant="ghost" onClick={()=>skoolStartEdit(row)} className="rounded-xl text-xs text-slate-600" data-testid={`skool-edit-${row.id}`}>Edit email</Button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="mailings">
          <MailingsErrorBoundary onReturnToOverview={() => setTab("users")}>
            <AdminMailings />
          </MailingsErrorBoundary>
        </TabsContent>

        <TabsContent value="credits">
          <AdminCredits />
        </TabsContent>

        <TabsContent value="audit">
          <Card className="glass rounded-2xl mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                  <tr><th className="p-3">Time</th><th className="p-3">User</th><th className="p-3">Action</th><th className="p-3">Meta</th></tr>
                </thead>
                <tbody data-testid="admin-audit-body">
                  {audit.map(a=>(
                    <tr key={a.id} className="border-b border-slate-200">
                      <td className="p-3 text-slate-700">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="p-3 text-slate-700">{a.user_id.slice(0,8)}…</td>
                      <td className="p-3 text-slate-900">{a.action}</td>
                      <td className="p-3 text-slate-500 font-mono text-xs">{JSON.stringify(a.meta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reasons">
          <Card className="glass rounded-2xl mt-4">
            <CardContent className="p-6">
              <div className="text-slate-900 font-semibold mb-2">Dispute reason options (one per line)</div>
              <Textarea rows={12} value={reasons} onChange={(e)=>setReasons(e.target.value)} className="bg-slate-50 border-slate-200 text-slate-900 font-mono text-[13px]" data-testid="admin-reasons-textarea"/>
              <Button className="btn-primary rounded-xl mt-3" onClick={saveReasons} data-testid="admin-reasons-save">Save reasons</Button>
            </CardContent>
          </Card>
          <Card className="glass rounded-2xl mt-4" data-testid="admin-ai-suggest-flag-card">
            <CardContent className="p-6 space-y-2">
              <div className="text-slate-900 font-semibold">AI dispute-reason assistant (beta feature flag)</div>
              <p className="text-xs text-slate-500 max-w-xl">When ON, an optional <b>Suggest dispute reasons</b> button appears under each account's explanation field. AI receives only creditor / account_type / account_status and the server-side-redacted explanation. Never modifies the existing Reasons list, never auto-submits, and always requires the client to affirm accuracy before generating letters. Ship OFF; enable only for controlled beta.</p>
              <label className="flex items-center gap-2 text-sm text-slate-800 mt-2">
                <Checkbox checked={!!aiFlag} onCheckedChange={(v)=>setAiFlag(!!v)} data-testid="admin-ai-suggest-flag-toggle" />
                Enable AI dispute-reason assistant
              </label>
              <Button className="btn-primary rounded-xl mt-2" onClick={saveAiFlag} data-testid="admin-ai-suggest-flag-save">Save flag</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="disclaimer">
          <Card className="glass rounded-2xl mt-4">
            <CardContent className="p-6">
              <div className="text-slate-900 font-semibold mb-2">Landing-page disclaimer text</div>
              <Textarea rows={5} value={disclaimer} onChange={(e)=>setDisclaimer(e.target.value)} className="bg-slate-50 border-slate-200 text-slate-900" data-testid="admin-disclaimer-textarea"/>
              <Button className="btn-primary rounded-xl mt-3" onClick={saveDisclaimer} data-testid="admin-disclaimer-save">Save disclaimer</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

function Stat({ label, v, t }) {
  return (
    <Card className="glass rounded-2xl"><CardContent className="p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl text-slate-900 mt-1 font-semibold" data-testid={t}>{v ?? 0}</div>
    </CardContent></Card>
  );
}

function AdminIdentityStorageSelfTest() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  // Presence-only readiness diagnostic. Never runs a self-test; never touches AWS.
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusErr, setStatusErr] = useState(null);
  const loadStatus = async () => {
    setStatusLoading(true); setStatus(null); setStatusErr(null);
    try {
      const r = await api.get("/admin/identity-storage/status");
      setStatus(r.data);
    } catch (e) {
      setStatusErr(e?.response?.data?.detail || e?.message || "Status check failed");
    } finally { setStatusLoading(false); }
  };
  // The Fernet-key generator uses browser crypto ONLY. The key is generated
  // inside the click handler, written straight to the clipboard, then the
  // local variable and source byte array are cleared. It is NEVER placed in
  // React state, DOM text, logs, analytics, or transmitted to the backend.
  const [keyCopied, setKeyCopied] = useState(false);
  const [keyErr, setKeyErr] = useState(null);
  const generateFernetKeyToClipboard = async () => {
    setKeyCopied(false); setKeyErr(null);
    const bytes = new Uint8Array(32);
    let key = null;
    try {
      if (!window.crypto || !window.crypto.getRandomValues) {
        throw new Error("Browser crypto unavailable — use a modern browser");
      }
      if (!navigator.clipboard || !navigator.clipboard.writeText) {
        throw new Error("Clipboard API unavailable — this page must be served over HTTPS");
      }
      window.crypto.getRandomValues(bytes);
      // urlsafe base64 (Fernet-compatible: 32 random bytes → 44 chars ending in "=")
      const bin = Array.from(bytes, (b) => String.fromCharCode(b)).join("");
      key = btoa(bin).replace(/\+/g, "-").replace(/\//g, "_");
      await navigator.clipboard.writeText(key);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 15000);
    } catch (e) {
      setKeyErr(e?.message || "Key generation failed");
    } finally {
      // Best-effort scrub. The string will still be GC'd on next tick.
      key = null;
      bytes.fill(0);
    }
  };
  const run = async () => {
    setRunning(true); setResult(null); setErr(null);
    try {
      const r = await api.post("/admin/identity-storage/self-test");
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Self-test failed");
    } finally { setRunning(false); }
  };
  return (
    <Card className="glass rounded-2xl" data-testid="admin-identity-selftest-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-slate-900 font-semibold">Identity storage — deletion self-test</div>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">Uploads a harmless canary blob, writes a second version, hard-deletes every version, and verifies <code>head_status: not_found</code>. Never touches a real member document. Use the pass evidence before flipping <code>IDENTITY_STORAGE_DELETE_VERIFIED=true</code> in production.</p>
          </div>
          <Button onClick={run} disabled={running} className="btn-primary rounded-xl" data-testid="admin-identity-selftest-run">
            {running ? <><RefreshCw className="animate-spin mr-1" size={14}/>Running…</> : "Run self-test"}
          </Button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Configuration status (presence-only, no AWS calls)</div>
              <p className="text-xs text-slate-500 mt-1 max-w-xl">Reports which identity-storage env vars are set/unset. Does not run the self-test. Does not touch AWS or member data.</p>
            </div>
            <Button onClick={loadStatus} disabled={statusLoading} variant="outline" className="rounded-xl border-slate-200 bg-slate-50 text-slate-800 whitespace-nowrap" data-testid="admin-identity-status-btn">
              {statusLoading ? "Loading…" : "Show config status"}
            </Button>
          </div>
          {statusErr && <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="admin-identity-status-err">{statusErr}</div>}
          {status && (
            <div className="mt-3 text-xs" data-testid="admin-identity-status-result">
              <div className="mb-2">
                <span className={status.delete_capable ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>
                  {status.delete_capable ? "✅ delete_capable=True" : "⚠️ delete_capable=False"}
                </span>
                <span className="text-slate-500 ml-2">scope=<b>{status.scope}</b> · identity_key_present=<b>{String(status.identity_key_present)}</b> · storage_delete_verified=<b>{String(status.storage_delete_verified)}</b></span>
              </div>
              <table className="w-full text-left text-slate-700">
                <tbody>
                  {Object.entries(status.checks || {}).map(([k, v]) => (
                    <tr key={k} className="border-b border-slate-100">
                      <td className="py-1 pr-2 font-mono text-[11px]">{k}</td>
                      <td className={`py-1 ${v ? "text-emerald-700" : "text-amber-700"}`}>{v ? "✅ set" : (k.endsWith("_optional") ? "— (optional, empty)" : "❌ unset")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {status.missing_or_wrong?.length > 0 && (
                <p className="mt-2 text-amber-700">Failing checks: <code>{status.missing_or_wrong.join(", ")}</code></p>
              )}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Generate <code>IDENTITY_DOCUMENTS_KEY</code> (founder-only, browser-only)</div>
              <p className="text-xs text-slate-500 mt-1 max-w-xl">
                Generates 32 random bytes via <code>crypto.getRandomValues()</code>, encodes them as a Fernet-compatible URL-safe Base64 key, and copies the value directly to your clipboard. The key is <b>never</b> sent to the backend, stored in React state, written to logs, analytics, MongoDB, source code, <code>.env</code>, or chat. Paste it into the <code>IDENTITY_DOCUMENTS_KEY</code> row of the Emergent Secrets tab and Save.
              </p>
            </div>
            <Button onClick={generateFernetKeyToClipboard} variant="outline" className="rounded-xl border-slate-200 bg-white text-slate-800 whitespace-nowrap" data-testid="admin-identity-genkey-btn">
              Generate & copy
            </Button>
          </div>
          {keyCopied && <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2" data-testid="admin-identity-genkey-ok">✅ Copied to clipboard. Paste into the <code>IDENTITY_DOCUMENTS_KEY</code> Secrets row, then Save. This banner clears in 15s. The key is not stored anywhere in this app.</div>}
          {keyErr && <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="admin-identity-genkey-err">{keyErr}</div>}
        </div>

        {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3" data-testid="admin-identity-selftest-error">{err}</div>}
        {result && (
          <div className={`rounded-xl border p-3 text-sm ${result.passed ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="admin-identity-selftest-result">
            <div className="font-semibold text-slate-900">{result.passed ? "PASSED — safe to flip IDENTITY_STORAGE_DELETE_VERIFIED=true" : "FAILED — do NOT flip the flag"}</div>
            <div className="text-xs text-slate-600 mt-1">Provider: <b>{result.provider}</b> · Flag currently: <b>{String(result.delete_verified_flag)}</b></div>
            <ol className="text-xs text-slate-700 mt-2 space-y-1 list-decimal ml-5">
              {(result.steps || []).map((s, i) => (
                <li key={i}>
                  <span className={s.ok ? "text-emerald-700" : "text-red-700"}>{s.ok ? "✅" : "❌"}</span> {s.step}
                  {typeof s.versions_deleted !== "undefined" && <span className="text-slate-500"> · versions_deleted={s.versions_deleted}</span>}
                  {typeof s.delete_markers_deleted !== "undefined" && <span className="text-slate-500"> · delete_markers={s.delete_markers_deleted}</span>}
                  {s.head_status && <span className="text-slate-500"> · head={s.head_status}</span>}
                </li>
              ))}
            </ol>
            <p className="text-[11px] text-slate-500 mt-2">{result.instruction}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AdminCredits() {
  const [cfg, setCfg] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [rec, setRec] = useState(null);
  const [q, setQ] = useState("");
  const [adjust, setAdjust] = useState({ user_id:"", amount_cents:0, reason:"" });
  const money = (c) => `$${((c ?? 0)/100).toFixed(2)}`;
  const load = async () => {
    const [s, r] = await Promise.all([api.get("/admin/credits/settings"), api.get("/admin/credits/reconcile")]);
    setCfg(s.data); setRec(r.data);
    const l = await api.get("/admin/credits/ledger" + (q?`?q=${encodeURIComponent(q)}`:""));
    setLedger(l.data);
  };
  useEffect(()=>{ load().catch(()=>toast.error("Load failed")); /* eslint-disable-next-line */ }, []);
  const save = async (patch) => { const r = await api.put("/admin/credits/settings", patch); setCfg({...cfg, ...r.data}); toast.success("Saved"); };
  const doAdjust = async () => {
    if (!adjust.user_id || !adjust.reason) return toast.error("User ID and reason required");
    await api.post(`/admin/credits/${adjust.user_id}/adjust`, { amount_cents: Number(adjust.amount_cents), reason: adjust.reason });
    setAdjust({ user_id:"", amount_cents:0, reason:"" }); toast.success("Adjustment posted"); load();
  };
  const exportCsv = () => {
    const token = localStorage.getItem("iat_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/credits/export.csv`, { headers:{Authorization:`Bearer ${token}`}})
      .then(r=>r.blob()).then(b=>{ const a=document.createElement("a"); a.href=URL.createObjectURL(b); a.download="credit_ledger.csv"; a.click(); a.remove(); });
  };
  if (!cfg) return <div className="p-6 text-slate-500">Loading…</div>;
  return (
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="glass rounded-2xl lg:col-span-1">
          <CardContent className="p-6 space-y-3">
            <div className="text-slate-900 font-semibold">Pricing & top-ups</div>
            <div><Label className="text-xs text-slate-700">Markup %</Label><Input type="number" step="0.1" value={cfg.markup_percent} onChange={(e)=>setCfg({...cfg, markup_percent: Number(e.target.value)})} onBlur={()=>save({markup_percent: cfg.markup_percent})} data-testid="admin-credit-markup" className="mt-1"/></div>
            <div><Label className="text-xs text-slate-700">Min service fee (¢)</Label><Input type="number" value={cfg.min_service_fee_cents} onChange={(e)=>setCfg({...cfg, min_service_fee_cents: Number(e.target.value)})} onBlur={()=>save({min_service_fee_cents: cfg.min_service_fee_cents})} data-testid="admin-credit-minfee" className="mt-1"/></div>
            <div><Label className="text-xs text-slate-700">Top-up amounts (¢, comma-separated)</Label>
              <Input defaultValue={(cfg.topup_amounts_cents||[]).join(",")} onBlur={(e)=>save({topup_amounts_cents: e.target.value.split(",").map(x=>Number(x.trim())).filter(Boolean)})} data-testid="admin-credit-amounts" className="mt-1"/></div>
            <div><Label className="text-xs text-slate-700">Low-balance threshold (¢)</Label><Input type="number" value={cfg.low_balance_threshold_cents} onChange={(e)=>setCfg({...cfg, low_balance_threshold_cents: Number(e.target.value)})} onBlur={()=>save({low_balance_threshold_cents: cfg.low_balance_threshold_cents})} className="mt-1"/></div>
            <label className="flex items-center gap-2 text-sm text-slate-800"><Switch checked={cfg.live_topups_enabled} onCheckedChange={(v)=>save({live_topups_enabled: v})} data-testid="admin-credit-live-topups"/>Enable LIVE Stripe top-ups</label>
            <label className="flex items-center gap-2 text-sm text-slate-800"><Switch checked={cfg.live_mailing_enabled} onCheckedChange={(v)=>save({live_mailing_enabled: v})} data-testid="admin-credit-live-mail"/>Enable LIVE Lob mailing</label>
            <div className="text-[11px] text-slate-500 pt-1">Stripe: <b>{cfg.stripe_mode?.toUpperCase()}</b> · Managed by Emergent Secrets · Secret {cfg.stripe_secret_configured?"✅":"❌"} · Publishable {cfg.stripe_publishable_configured?"✅":"❌"} · Webhook {cfg.stripe_webhook_configured?"✅":"❌"}</div>
          </CardContent>
        </Card>

        <Card className="glass rounded-2xl lg:col-span-2">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between"><div className="text-slate-900 font-semibold">Reconciliation</div>
              <Button size="sm" variant="ghost" onClick={load}>Refresh</Button></div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <Kpi k="Stripe paid" v={money(rec?.stripe_paid_sessions_cents)}/>
              <Kpi k="Ledger top-ups" v={money(rec?.ledger_topups_cents)} warn={rec?.topup_delta_cents!==0}/>
              <Kpi k="Mailing debits" v={money(Math.abs(rec?.ledger_mailing_debits_cents||0))}/>
              <Kpi k="Refunds" v={money(rec?.ledger_refunds_cents)}/>
              <Kpi k="Sum balances" v={money(rec?.sum_balances_cents)}/>
              <Kpi k="Computed" v={money(rec?.computed_balance_cents)} warn={rec?.balance_delta_cents!==0}/>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={exportCsv} className="btn-primary rounded-xl" data-testid="admin-credit-export"><Download size={13} className="mr-1"/>Export CSV</Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <AdminIdentityStorageSelfTest />

      <Card className="glass rounded-2xl">
        <CardContent className="p-6 space-y-3">
          <div className="text-slate-900 font-semibold">Manual adjustment</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <Input placeholder="User ID" value={adjust.user_id} onChange={(e)=>setAdjust({...adjust, user_id: e.target.value})} data-testid="admin-adjust-user"/>
            <Input placeholder="Amount cents (+/-)" type="number" value={adjust.amount_cents} onChange={(e)=>setAdjust({...adjust, amount_cents: e.target.value})} data-testid="admin-adjust-amount"/>
            <Input className="md:col-span-2" placeholder="Reason (required)" value={adjust.reason} onChange={(e)=>setAdjust({...adjust, reason: e.target.value})} data-testid="admin-adjust-reason"/>
          </div>
          <Button onClick={doAdjust} className="btn-primary rounded-xl" data-testid="admin-adjust-submit">Post adjustment</Button>
          <p className="text-[11px] text-slate-500">Adjustments append to the ledger — they cannot silently edit history.</p>
        </CardContent>
      </Card>

      <Card className="glass rounded-2xl">
        <CardContent className="p-0 overflow-x-auto">
          <div className="p-4 flex items-center gap-2">
            <Input placeholder="Search Stripe session, PI, Lob ID, or user ID" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="admin-credit-search"/>
            <Button size="sm" onClick={load} data-testid="admin-credit-search-go">Search</Button>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
              <tr><th className="p-3">When</th><th className="p-3">User</th><th className="p-3">Type</th><th className="p-3 text-right">Amount</th><th className="p-3 text-right">Balance after</th><th className="p-3">Stripe</th><th className="p-3">Lob</th><th className="p-3">Reason</th></tr>
            </thead>
            <tbody data-testid="admin-credit-ledger">
              {ledger.map(r=>(
                <tr key={r.id} className="border-b border-slate-200">
                  <td className="p-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="p-3 text-xs text-slate-700 font-mono">{r.user_id?.slice(0,8)}…</td>
                  <td className="p-3"><span className="stage-pill rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest">{r.type}</span></td>
                  <td className={`p-3 text-right font-mono ${r.amount_cents>=0?"text-emerald-700":"text-red-700"}`}>{r.amount_cents>=0?"+":""}{money(r.amount_cents)}</td>
                  <td className="p-3 text-right text-slate-900">{money(r.balance_after_cents)}</td>
                  <td className="p-3 text-xs text-slate-500 font-mono">{r.stripe_session_id ? r.stripe_session_id.slice(0,14)+"…" : "—"}</td>
                  <td className="p-3 text-xs text-slate-500 font-mono">{r.lob_mailing_id || "—"}</td>
                  <td className="p-3 text-xs text-slate-500">{r.reason || "—"}</td>
                </tr>
              ))}
              {ledger.length===0 && <tr><td colSpan="8" className="p-6 text-slate-500 text-center">No transactions yet.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
function Kpi({ k, v, warn }) {
  return (<div className={`rounded-xl border p-3 ${warn?"border-amber-300 bg-amber-50":"border-slate-200 bg-slate-50"}`}>
    <div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div>
    <div className="text-slate-900 font-semibold mt-1">{v}</div>
  </div>);
}

// ---------- AdminMailings hardening helpers (localized to this file) ----------
function safeStr(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
}
function safeUpper(v) {
  const s = safeStr(v);
  return s ? s.toUpperCase() : "";
}
function safeSlice(v, n) {
  const s = safeStr(v);
  return s ? s.slice(0, n) : "";
}
function formatDate(iso) {
  const s = safeStr(iso);
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  try { return d.toLocaleString(); } catch { return "—"; }
}
function formatPrice(row) {
  if (!row || typeof row !== "object") return "—";
  const cents = row.retail_price_cents;
  if (typeof cents === "number" && Number.isFinite(cents)) {
    return `$${(cents / 100).toFixed(2)}`;
  }
  const legacy = row.price;
  if (typeof legacy === "number" && Number.isFinite(legacy)) {
    return `$${legacy.toFixed(2)}`;
  }
  if (typeof legacy === "string" && legacy.trim() && !Number.isNaN(Number(legacy))) {
    return `$${Number(legacy).toFixed(2)}`;
  }
  return "—";
}
function rowKey(row, idx) {
  if (row && typeof row === "object") {
    if (typeof row.id === "string" && row.id) return row.id;
    if (typeof row.lob_id === "string" && row.lob_id) return `lob-${row.lob_id}`;
    const composite = [safeStr(row.user_id), safeStr(row.created_at), safeStr(row.mail_type)].filter(Boolean).join("|");
    if (composite) return `c-${composite}`;
  }
  return `mail-${idx}`;
}

function AdminMailings() {
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const [m, s] = await Promise.all([
        api.get("/admin/mailings"),
        api.get("/admin/mailings/settings"),
      ]);
      const list = Array.isArray(m?.data) ? m.data.filter(x => x && typeof x === "object") : [];
      setRows(list);
      setSettings(s?.data && typeof s.data === "object" ? s.data : null);
    } catch (e) {
      // Never surface backend detail, stack traces, or PII in the UI.
      setLoadErr("We couldn’t load mailings right now. Please try again.");
      setRows([]);
      setSettings(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setPolicy = async (v) => {
    try {
      await api.put("/admin/mailings/settings", { policy: v });
      toast.success("Policy saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save policy");
    }
  };

  const safeRows = Array.isArray(rows) ? rows : [];
  const failures = safeRows.filter(r => r && r.status === "error");
  const modeUpper = safeUpper(settings?.mode);
  const modeClass = safeStr(settings?.mode) === "live" ? "text-emerald-700" : "text-amber-700";
  const policies = Array.isArray(settings?.policies) ? settings.policies.filter(p => p && typeof p === "object") : [];

  return (
    <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="glass rounded-2xl">
        <CardContent className="p-6 space-y-3">
          <div className="text-slate-900 font-semibold">Lob configuration</div>
          <div className="text-xs text-slate-600 grid grid-cols-2 gap-2">
            <div>Mode</div><div className={modeClass}><b>{modeUpper || "—"}</b></div>
            <div>Test key</div><div>{settings?.test_key_configured ? "✅" : "❌"}</div>
            <div>Live key</div><div>{settings?.live_key_configured ? "✅" : "❌"}</div>
            <div>Webhook secret</div><div>{settings?.webhook_configured ? "✅" : "❌"}</div>
          </div>
          <div>
            <div className="text-xs text-slate-600 mt-3 mb-1">Payment policy</div>
            <div className="space-y-1">
              {policies.map(p => (
                <label key={safeStr(p.value) || Math.random().toString(36)} className="flex items-center gap-2 text-sm text-slate-800">
                  <input
                    type="radio"
                    name="policy"
                    checked={settings?.payment_policy === p.value}
                    onChange={() => setPolicy(p.value)}
                    data-testid={`admin-policy-${safeStr(p.value) || "unknown"}`}
                  />
                  {safeStr(p.label) || safeStr(p.value)}
                </label>
              ))}
            </div>
            {!settings?.payment_policy && (
              <p className="text-[11px] text-red-700 mt-2">Live submission is disabled until a policy is selected.</p>
            )}
          </div>
          <Link to="/app/admin/lob" data-testid="admin-open-lob-setup">
            <Button variant="outline" className="rounded-xl w-full border-slate-200 bg-slate-50/70 text-slate-800 mt-3">Open Lob integration setup →</Button>
          </Link>
        </CardContent>
      </Card>
      <Card className="glass rounded-2xl lg:col-span-2">
        <CardContent className="p-0 overflow-x-auto">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div className="text-xs text-slate-500">
              {loading ? "Loading mailings…" : `${safeRows.length} mailing${safeRows.length === 1 ? "" : "s"}`}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="admin-mailings-refresh"
              className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800 text-xs"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`}/>
              {loading ? "Loading" : "Refresh"}
            </Button>
          </div>
          {loadErr && (
            <div className="p-4 border-b border-slate-200 text-xs text-red-700 flex items-center justify-between" data-testid="admin-mailings-error">
              <span>{loadErr}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={load}
                data-testid="admin-mailings-retry"
                className="rounded-xl border-red-200 bg-red-50 text-red-800 text-xs"
              >Retry</Button>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
              <tr><th className="p-3">When</th><th className="p-3">User</th><th className="p-3">Lob ID</th><th className="p-3">Status</th><th className="p-3">Class</th><th className="p-3">Price</th><th className="p-3">Tracking</th></tr>
            </thead>
            <tbody data-testid="admin-mailings-body">
              {loading && safeRows.length === 0 && (
                <tr><td colSpan="7" className="p-6 text-slate-500 text-center">Loading…</td></tr>
              )}
              {!loading && !loadErr && safeRows.length === 0 && (
                <tr><td colSpan="7" className="p-6 text-slate-500 text-center">No mailings yet.</td></tr>
              )}
              {safeRows.map((r, idx) => {
                const uid = safeSlice(r?.user_id, 8);
                const lobId = safeStr(r?.lob_id);
                const status = safeStr(r?.lob_status) || safeStr(r?.status) || "—";
                const isError = safeStr(r?.status) === "error";
                const mailType = safeStr(r?.mail_type);
                const extra = safeStr(r?.extra_service);
                const tracking = safeStr(r?.tracking_number);
                return (
                  <tr key={rowKey(r, idx)} className="border-b border-slate-200">
                    <td className="p-3 text-xs text-slate-500">{formatDate(r?.created_at)}</td>
                    <td className="p-3 text-slate-700 text-xs">{uid ? `${uid}…` : "—"}</td>
                    <td className="p-3 text-slate-700 font-mono text-xs">{lobId || "—"}</td>
                    <td className="p-3">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${isError ? "badge-danger" : "badge-success"}`}>
                        {status}
                      </span>
                    </td>
                    <td className="p-3 text-slate-700 text-xs">
                      {mailType || "—"}{extra ? ` · ${extra}` : ""}
                    </td>
                    <td className="p-3 text-slate-700 text-xs">{formatPrice(r)}</td>
                    <td className="p-3 text-slate-700 text-xs">{tracking || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {failures.length > 0 && (
            <div className="p-4 border-t border-slate-200 text-xs text-red-700">
              {failures.length} failed mailing(s). Use API POST /api/admin/mailings/&#123;id&#125;/resend to retry.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------- Localized error boundary for the Mailings tab only ----------
class MailingsErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, ref: "" };
  }
  static getDerivedStateFromError() {
    const ts = Date.now().toString(36);
    const rand = Math.random().toString(36).slice(2, 6);
    return { hasError: true, ref: `err-${ts}-${rand}` };
  }
  componentDidCatch(error, info) {
    // Intentionally do not log record contents or stack traces to the console
    // to avoid leaking PII (names, addresses, tracking numbers, tokens).
    try {
      // eslint-disable-next-line no-console
      console.warn("[AdminMailings] render error captured by boundary", this.state.ref);
    } catch (_) { /* noop */ }
  }
  handleRetry = () => {
    this.setState({ hasError: false, ref: "" });
  };
  handleReturn = () => {
    this.setState({ hasError: false, ref: "" });
    if (typeof this.props.onReturnToOverview === "function") {
      this.props.onReturnToOverview();
    }
  };
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="mt-4" data-testid="admin-mailings-boundary">
        <Card className="glass rounded-2xl border border-red-200">
          <CardContent className="p-6 space-y-3">
            <div className="text-slate-900 font-semibold">The Mailings page encountered an error</div>
            <p className="text-sm text-slate-600">
              The rest of the Admin portal is still available. You can retry loading Mailings, or return to the Admin overview.
            </p>
            <div className="text-[11px] text-slate-500 font-mono">Diagnostic ref: {this.state.ref || "err-unknown"}</div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                onClick={this.handleRetry}
                data-testid="admin-mailings-boundary-retry"
                className="rounded-xl bg-slate-900 text-white hover:bg-slate-800"
              >Try Again</Button>
              <Button
                variant="outline"
                onClick={this.handleReturn}
                data-testid="admin-mailings-boundary-return"
                className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800"
              >Return to Admin Overview</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}
